import { useRef } from "react";
import type { ArrowCorner, ShapeCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { resolveArrowPath } from "../../../lib/shape-arrow";

interface Props {
  readonly object: ShapeCanvasObject;
}

// 図形の既定の輪郭色。選択時はアクセント色でハイライトする。
const SHAPE_DEFAULT_STROKE = "#1a1a2e";
// SVG 描画上の矢じりサイズ（軸方向の長さ / 直交方向の片側張り出し）。
const ARROW_HEAD_LENGTH = 12;
const ARROW_HEAD_HALF = 6;

// 図形オブジェクト（丸 / 四角 / 矢印）。SVG で描画する。
// クリックで選択、ドラッグで移動できる。SVG 自体に pointer events を付けて
// 内側 fill が transparent でもヒットを取れるよう pointerEvents="all" を付与する。
export function ShapeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  const stroke = isSelected ? "var(--color-accent)" : object.stroke ?? SHAPE_DEFAULT_STROKE;

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="shape"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        overflow: "visible",
      }}
      // リサイズ中に viewBox が更新されない瞬間でも、内部要素を常に SVG の表示寸法へ
      // 等比展開させたい。preserveAspectRatio="none" で縦横独立に伸縮し、
      // viewBox の値に依存せずドロップ後の最終図形と同じ見た目をプレビューする。
      viewBox={`0 0 ${object.w} ${object.h}`}
      preserveAspectRatio="none"
    >
      <ShapeGlyph
        shapeKind={object.shapeKind}
        w={object.w}
        h={object.h}
        fill={object.fill ?? "transparent"}
        stroke={stroke}
        strokeWidth={isSelected ? 3 : 2}
        arrowFromCorner={object.arrowFromCorner}
      />
    </svg>
  );
}

/**
 * 図形の内側描画要素（丸 / 四角 / 矢印）。
 * 確定済みオブジェクト（ShapeObject）とドラッグ配置中のプレビュー（CanvasShapePlacementOverlay）で
 * 共有し、見た目を一致させる。座標はすべて呼び出し側の SVG（viewBox 0 0 w h）に追従する。
 */
export function ShapeGlyph({
  shapeKind,
  w,
  h,
  fill,
  stroke,
  strokeWidth,
  arrowFromCorner,
  pointerEvents = "all",
}: {
  readonly shapeKind: ShapeCanvasObject["shapeKind"];
  readonly w: number;
  readonly h: number;
  readonly fill: string;
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly arrowFromCorner?: ArrowCorner | undefined;
  readonly pointerEvents?: "all" | "none" | undefined;
}): JSX.Element {
  if (shapeKind === "circle") {
    // 100% 基準で SVG 表示寸法に追従させ、ドラッグ中の枠と中身を一致させる。
    // stroke は vector-effect で非スケールに固定し、伸縮しても太さが均一に保たれる。
    return (
      <ellipse
        cx="50%"
        cy="50%"
        rx="50%"
        ry="50%"
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      />
    );
  }
  if (shapeKind === "rectangle") {
    // 矩形も 100% で SVG を埋めて、リサイズ中の輪郭が選択枠と一致するようにする。
    return (
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      />
    );
  }
  // 矢印。arrowFromCorner が定義済み（ドラッグ配置）なら起点角→対角へ描く。
  // 未指定（シングルクリック配置・旧データ）は従来どおり水平左→右で描く。
  // preserveAspectRatio="none" 下で viewBox を等比伸縮するため、矢じり先端の絶対サイズが
  // 多少伸縮するが、ドロップ後の最終形に近いゴースト表示となる。
  if (arrowFromCorner) {
    const path = resolveArrowPath(w, h, arrowFromCorner, ARROW_HEAD_LENGTH, ARROW_HEAD_HALF);
    return (
      <g
        stroke={stroke}
        fill="none"
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      >
        <line x1={path.tail.x} y1={path.tail.y} x2={path.head.x} y2={path.head.y} />
        <polyline
          points={`${path.wingA.x},${path.wingA.y} ${path.head.x},${path.head.y} ${path.wingB.x},${path.wingB.y}`}
        />
      </g>
    );
  }
  return (
    <g
      stroke={stroke}
      fill="none"
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      pointerEvents={pointerEvents}
    >
      <line x1={4} y1={h / 2} x2={w - 12} y2={h / 2} />
      <polyline
        points={`${w - 12},${h / 2 - 6} ${w - 4},${h / 2} ${w - 12},${h / 2 + 6}`}
      />
    </g>
  );
}
